import { apiFetch } from "@/auth/apiClient";

export interface ExamConfig {
  id: string;
  code: string;
  name: string;
  description?: string;
  status: 'ACTIVE' | 'INACTIVE' | 'ARCHIVED';
  passCriteria: 'OVERALL_ONLY' | 'SECTIONAL_REQUIRED';
  sortOrder: number;
  levels?: ExamLevel[];
  sections?: ExamSection[];
  mockTestRules?: string;
  createdAt: string;
  updatedAt: string;
}

export interface ExamLevel {
  id: string;
  examConfigId: string;
  code: string;
  name: string;
  status: 'ACTIVE' | 'INACTIVE' | 'ARCHIVED';
  sortOrder: number;
  createdAt: string;
  updatedAt: string;
}

export interface ExamSection {
  id: string;
  examConfigId: string;
  code: string;
  name: string;
  japaneseName?: string;
  englishName?: string;
  description?: string;
  status: 'ACTIVE' | 'INACTIVE' | 'ARCHIVED';
  sortOrder: number;
  createdAt: string;
  updatedAt: string;
}

export const examConfigApi = {
  /**
   * Public / Teacher / Admin endpoint to fetch all active configs
   */
  getActive: async (): Promise<ExamConfig[]> => {
    return apiFetch('/exam-config');
  },

  /**
   * Admin endpoint to fetch all configs including inactive
   */
  getAll: async (): Promise<ExamConfig[]> => {
    return apiFetch('/exam-config/all');
  },

  // Config CRUD
  createConfig: async (data: Partial<ExamConfig>): Promise<ExamConfig> => {
    const { id, createdAt, updatedAt, levels, sections, ...payload } = data as any;
    return apiFetch('/exam-config', { method: 'POST', json: payload });
  },
  updateConfig: async (id: string, data: Partial<ExamConfig>): Promise<ExamConfig> => {
    const { id: _, createdAt, updatedAt, levels, sections, ...payload } = data as any;
    return apiFetch(`/exam-config/${id}`, { method: 'PUT', json: payload });
  },
  deleteConfig: async (id: string): Promise<void> => {
    return apiFetch(`/exam-config/${id}`, { method: 'DELETE' });
  },

  // Level CRUD
  createLevel: async (data: Partial<ExamLevel>): Promise<ExamLevel> => {
    const { id, createdAt, updatedAt, ...payload } = data as any;
    return apiFetch('/exam-config/level', { method: 'POST', json: payload });
  },
  updateLevel: async (id: string, data: Partial<ExamLevel>): Promise<ExamLevel> => {
    const { id: _, createdAt, updatedAt, ...payload } = data as any;
    return apiFetch(`/exam-config/level/${id}`, { method: 'PUT', json: payload });
  },
  deleteLevel: async (id: string): Promise<void> => {
    return apiFetch(`/exam-config/level/${id}`, { method: 'DELETE' });
  },

  // Section CRUD
  createSection: async (data: Partial<ExamSection>): Promise<ExamSection> => {
    const { id, createdAt, updatedAt, ...payload } = data as any;
    return apiFetch('/exam-config/section', { method: 'POST', json: payload });
  },
  updateSection: async (id: string, data: Partial<ExamSection>): Promise<ExamSection> => {
    const { id: _, createdAt, updatedAt, ...payload } = data as any;
    return apiFetch(`/exam-config/section/${id}`, { method: 'PUT', json: payload });
  },
  deleteSection: async (id: string): Promise<void> => {
    return apiFetch(`/exam-config/section/${id}`, { method: 'DELETE' });
  },
  // Usage Analytics
  getExamTypeUsage: async (id: string): Promise<{ courses: number, mockTests: number, courseExams: number, studentAttempts: number, totalDependencies: number }> => {
    return apiFetch(`/exam-config/${id}/usage`);
  },
  getLevelUsage: async (id: string): Promise<{ courses: number, mockTests: number, courseExams: number, studentAttempts: number, totalDependencies: number }> => {
    return apiFetch(`/exam-config/levels/${id}/usage`);
  },
  getSectionUsage: async (id: string): Promise<{ questions: number, mockTestQuestions: number, mockTests: number, totalDependencies: number }> => {
    return apiFetch(`/exam-config/sections/${id}/usage`);
  },
};

